import jsPDF from 'jspdf'
import authCodeTextImg from '../static/img/auth-code-text.png'

const PDF_FILENAME = 'authorization-code.pdf'

const buildDeepLink = (code) =>
  `${window.location.origin}${window.location.pathname}?code=${encodeURIComponent(code)}`

// Opens a jsPDF document in a new tab in a way that survives Safari, iOS
// Safari, and Android Chrome. jsPDF's built-in `dataurlnewwindow` writes a
// `data:` URI inside an iframe via `document.write`, which renders blank
// tabs on iOS and intermittently on desktop Safari. Using a blob URL and a
// direct `window.open` (with an anchor-click fallback for popup-blocked
// cases) is the pattern that works reliably across platforms. Must be
// called synchronously from a user gesture so the popup isn't blocked.
const openPdfInNewTab = (pdf, fileName = PDF_FILENAME) => {
  const blob = pdf.output('blob')
  const url = URL.createObjectURL(blob)

  const newWindow = window.open(url, '_blank', 'noopener,noreferrer')

  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

// Server-verify path: the code is canonical, so we can embed a working
// deep link and lay the page out properly with a heading, code box, and
// styled call-to-action button.
const renderServerPathPDF = (codeToDisplay, deepLink) => {
  // eslint-disable-next-line new-cap
  const pdf = new jsPDF()

  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(22)
  pdf.setTextColor(20, 50, 100)
  pdf.text('Your Appraisal Authorization Code', 105, 35, { align: 'center' })

  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(11)
  pdf.setTextColor(60, 60, 60)
  const intro = pdf.splitTextToSize(
    'Use this code to confirm your identity and access the electronic copy of your appraisal report.',
    150
  )
  pdf.text(intro, 105, 48, { align: 'center' })

  pdf.setFillColor(245, 247, 250)
  pdf.setDrawColor(220, 225, 232)
  pdf.roundedRect(50, 65, 110, 28, 3, 3, 'FD')
  pdf.setFont('courier', 'bold')
  pdf.setFontSize(28)
  pdf.setTextColor(20, 50, 100)
  pdf.text(codeToDisplay, 105, 84, { align: 'center' })

  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(13)
  pdf.setTextColor(20, 50, 100)
  pdf.text('How to download your appraisal', 20, 112)

  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(11)
  pdf.setTextColor(60, 60, 60)
  const opt1 = pdf.splitTextToSize(
    'Option 1: Copy the code above and enter it on the previous screen, then click "Download Appraisal".',
    170
  )
  pdf.text(opt1, 20, 122)
  const opt2 = pdf.splitTextToSize(
    'Option 2: Click the button below to prefill the code and start your download automatically.',
    170
  )
  pdf.text(opt2, 20, 138)

  const btnX = 65
  const btnY = 158
  const btnW = 80
  const btnH = 14
  pdf.setFillColor(36, 110, 184)
  pdf.roundedRect(btnX, btnY, btnW, btnH, 3, 3, 'F')
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(13)
  pdf.setTextColor(255, 255, 255)
  pdf.text('Download Appraisal', btnX + btnW / 2, btnY + btnH / 2 + 1.5, { align: 'center' })
  pdf.link(btnX, btnY, btnW, btnH, { url: deepLink })

  // Backup plain link — survives PDF viewers that don't render overlay
  // links on filled shapes. Wrap long URLs so they don't run past the
  // right margin.
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(9)
  pdf.setTextColor(100, 100, 100)
  pdf.text("If the button doesn't work, paste this link into your browser:", 105, 188, { align: 'center' })
  pdf.setTextColor(36, 110, 184)
  const linkLines = pdf.splitTextToSize(deepLink, 170)
  const linkLineHeight = 4
  linkLines.forEach((line, i) => {
    const lineWidth = pdf.getTextWidth(line)
    const lineX = (210 - lineWidth) / 2
    pdf.textWithLink(line, lineX, 194 + i * linkLineHeight, { url: deepLink })
  })

  openPdfInNewTab(pdf)
}

const buildLegacyPdf = (img, codeToDisplay) => {
  // eslint-disable-next-line new-cap
  const pdf = new jsPDF()
  pdf.addImage(img, 'PNG', 20, 40, 129, 43)
  pdf.text(20, 90, `\t${codeToDisplay}`)
  return pdf
}

// Legacy path: the in-memory uuid is regenerated on every page load, so
// a deep link from a previous session would never match. Keep the
// original image-based layout for backwards compatibility, but route
// through the same blob-URL opener as the server path so behavior is
// consistent across browsers, and surface an error if the bundled image
// somehow fails to load (rather than hanging silently).
const renderLegacyPathPDF = (codeToDisplay) =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      try {
        openPdfInNewTab(buildLegacyPdf(img, codeToDisplay))
        resolve()
      } catch (err) {
        reject(err)
      }
    }
    img.onerror = () => reject(new Error('Failed to load authorization code image'))
    img.src = authCodeTextImg
  })

export const generateConsentPDF = ({ code, legacyAuthCode }) => {
  const codeToDisplay = code || legacyAuthCode
  if (code) {
    renderServerPathPDF(codeToDisplay, buildDeepLink(code))
    return Promise.resolve()
  }
  return renderLegacyPathPDF(codeToDisplay)
}
